// src/pages/HapiGuard.jsx
import { useState, useEffect, useRef, useCallback } from 'react';
import DateRangePicker from '../components/ui/DateRangePicker';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import {
  ShieldCheck, ShieldAlert, ShieldOff, Copy, Check,
  ExternalLink, RefreshCw, Plus, AlertTriangle,
  Globe, MousePointerClick, Ban, Mail, Lock,
  Users, Eye, EyeOff, Clock, Calendar, ChevronLeft, ChevronRight, Monitor, Save,
  ChevronDown, X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, BarChart, Bar, Legend,
} from 'recharts';

// ─── Auth fetch ───────────────────────────────────────────────────────────────
async function authFetch(action, options = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  const qs  = options.qs ? `&${options.qs}` : '';
  return fetch(`/api/hapiguard?action=${action}${qs}`, {
    method: 'GET',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
}

// ─── Date helpers ─────────────────────────────────────────────────────────────
const fmt      = d => d instanceof Date ? d.toISOString().split('T')[0] : d;
const addDays  = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };
const startOM  = d => new Date(d.getFullYear(), d.getMonth(), 1);
const endOM    = d => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const sameDay  = (a, b) => fmt(a) === fmt(b);
const inRange  = (d, s, e) => fmt(d) >= fmt(s) && fmt(d) <= fmt(e);

function formatTime(s) {
  if (!s) return '—';
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60); const r = s % 60;
  return r > 0 ? `${m}m ${r}s` : `${m}m`;
}
function fmtDateRange(s, e) {
  const opts = { month: 'short', day: 'numeric' };
  if (fmt(s) === fmt(e)) return new Date(s).toLocaleDateString('en', { ...opts, year: 'numeric' });
  return `${new Date(s).toLocaleDateString('en', opts)} – ${new Date(e).toLocaleDateString('en', { ...opts, year: 'numeric' })}`;
}

const DATE_PRESETS = [
  { label: 'Today',      value: 'today',    getDates: () => { const t = new Date(); return { s: t, e: t }; } },
  { label: 'Yesterday',  value: 'yesterday',getDates: () => { const t = addDays(new Date(),-1); return { s:t, e:t }; } },
  { label: 'Last 7d',   value: '7d',       getDates: () => ({ s: addDays(new Date(),-6), e: new Date() }) },
  { label: 'Last 30d',  value: '30d',      getDates: () => ({ s: addDays(new Date(),-29), e: new Date() }) },
  { label: 'Last 90d',  value: '90d',      getDates: () => ({ s: addDays(new Date(),-89), e: new Date() }) },
];

// ─── COUNTRIES ────────────────────────────────────────────────────────────────
const COUNTRIES = [
  {code:'AF',name:'Afghanistan'},{code:'AL',name:'Albania'},{code:'DZ',name:'Algeria'},
  {code:'AR',name:'Argentina'},{code:'AU',name:'Australia'},{code:'AT',name:'Austria'},
  {code:'BD',name:'Bangladesh'},{code:'BE',name:'Belgium'},{code:'BR',name:'Brazil'},
  {code:'CA',name:'Canada'},{code:'CN',name:'China'},{code:'CO',name:'Colombia'},
  {code:'EG',name:'Egypt'},{code:'FR',name:'France'},{code:'DE',name:'Germany'},
  {code:'GH',name:'Ghana'},{code:'IN',name:'India'},{code:'ID',name:'Indonesia'},
  {code:'IR',name:'Iran'},{code:'IE',name:'Ireland'},{code:'IL',name:'Israel'},
  {code:'IT',name:'Italy'},{code:'JP',name:'Japan'},{code:'KE',name:'Kenya'},
  {code:'MX',name:'Mexico'},{code:'MA',name:'Morocco'},{code:'NL',name:'Netherlands'},
  {code:'NG',name:'Nigeria'},{code:'NO',name:'Norway'},{code:'PK',name:'Pakistan'},
  {code:'PH',name:'Philippines'},{code:'PL',name:'Poland'},{code:'PT',name:'Portugal'},
  {code:'RO',name:'Romania'},{code:'RU',name:'Russia'},{code:'SA',name:'Saudi Arabia'},
  {code:'ZA',name:'South Africa'},{code:'ES',name:'Spain'},{code:'SE',name:'Sweden'},
  {code:'CH',name:'Switzerland'},{code:'TW',name:'Taiwan'},{code:'TH',name:'Thailand'},
  {code:'TR',name:'Turkey'},{code:'UA',name:'Ukraine'},{code:'AE',name:'UAE'},
  {code:'GB',name:'United Kingdom'},{code:'US',name:'United States'},
  {code:'VN',name:'Vietnam'},{code:'YE',name:'Yemen'},
];

// ═══════════════════════════════════════════════════════════════════════════════
// SKELETONS
// ═══════════════════════════════════════════════════════════════════════════════
function Skeleton({ className = '' }) {
  return <div className={`animate-pulse bg-muted rounded-xl ${className}`} />;
}

function PageSkeleton() {
  return (
    <div className="space-y-5">
      {/* Header row */}
      <div className="flex items-center gap-3 flex-wrap">
        <Skeleton className="h-7 w-32" />
        <Skeleton className="h-9 w-36" />
        <div className="flex-1" />
        <Skeleton className="h-9 w-40" />
        <Skeleton className="h-9 w-24" />
      </div>
      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 gap-3">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="rounded-2xl border border-border bg-card overflow-hidden">
            <div className="h-0.5 bg-muted" />
            <div className="p-3 space-y-2">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-6 w-12" />
            </div>
          </div>
        ))}
      </div>
      {/* Chart */}
      <div className="rounded-2xl border border-border bg-card p-5">
        <Skeleton className="h-4 w-24 mb-4" />
        <Skeleton className="h-48 w-full" />
      </div>
      {/* Two cards */}
      <Skeleton className="h-24 w-full rounded-2xl" />
      <Skeleton className="h-48 w-full rounded-2xl" />
    </div>
  );
}

function StatsSkeleton() {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 gap-3">
      {Array.from({ length: 7 }).map((_, i) => (
        <div key={i} className="rounded-2xl border border-border bg-card overflow-hidden">
          <div className="h-0.5 bg-muted" />
          <div className="p-3 space-y-2">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-6 w-12" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SITE SELECTOR DROPDOWN
// ═══════════════════════════════════════════════════════════════════════════════
function SiteSelector({ sites, selected, onSelect, onDelete }) {
  const [open, setOpen] = useState(false);
  const current = sites.find(s => s.id === selected);
  const ref = useRef(null);

  useEffect(() => {
    const fn = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-3 py-2 rounded-xl border
          border-border bg-card text-foreground
          hover:bg-muted transition-colors text-sm font-medium min-w-[180px]"
      >
        <Globe className="h-4 w-4 text-emerald-500 shrink-0" />
        <span className="flex-1 text-left truncate">{current?.domain || 'Select site'}</span>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1.5 z-50 min-w-[220px]
          bg-card border border-border rounded-xl shadow-lg overflow-hidden">
          {sites.map(site => (
            <div
              key={site.id}
              className={`flex items-center justify-between group px-3 py-2.5 cursor-pointer transition-colors
                ${site.id === selected ? 'bg-muted' : 'hover:bg-muted'}`}
              onClick={() => { onSelect(site.id); setOpen(false); }}
            >
              <div className="flex items-center gap-2 min-w-0">
                <Globe className={`h-3.5 w-3.5 shrink-0 ${site.id === selected ? 'text-emerald-500' : 'text-muted-foreground'}`} />
                <span className={`text-sm truncate ${site.id === selected ? 'font-semibold text-foreground' : 'text-foreground'}`}>
                  {site.domain}
                </span>
              </div>
              <button
                onClick={e => { e.stopPropagation(); onDelete(site.id, site.domain); setOpen(false); }}
                className="opacity-0 group-hover:opacity-100 ml-2 w-6 h-6 shrink-0
                  flex items-center justify-center rounded-lg
                  hover:bg-destructive/15 transition-all"
                title="Delete site"
              >
                <X className="h-3.5 w-3.5 text-destructive" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════
export default function HapiGuardPage() {
  const { isPro } = useAuth();

  const [sites,        setSites]        = useState([]);
  const [selected,     setSelected]     = useState(null);
  const [stats,        setStats]        = useState(null);
  const [settings,     setSettings]     = useState(null);
  const [pageLoading,  setPageLoading]  = useState(true);   // initial full-page load
  const [statsLoading, setStatsLoading] = useState(false);  // only stats area
  const [saving,       setSaving]       = useState(false);
  const [addingDomain, setAddingDomain] = useState(false);
  const [newDomain,    setNewDomain]    = useState('');
  const [copied,       setCopied]       = useState(false);
  const [planInfo,     setPlanInfo]     = useState({ isPro: false });
  const [blockedList,  setBlockedList]  = useState([]);
  const [blockedLoading, setBlockedLoading] = useState(false);

  const [dateRange, setDateRange] = useState(() => {
    const now = new Date();
    const y = now.getFullYear(), m = String(now.getMonth()+1).padStart(2,'0'), d = String(now.getDate()).padStart(2,'0');
    const today = `${y}-${m}-${d}`;
    return { start: today, end: today, preset: 'today' };
  });

  useEffect(() => { fetchSites(); }, []);

  useEffect(() => {
    if (selected) { fetchStats(); fetchSettings(); fetchBlockedList(selected); }
  }, [selected, dateRange]);

  async function fetchSites() {
    setPageLoading(true);
    try {
      const res  = await authFetch('sites');
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setSites(data.sites || []);
      if (data.plan) setPlanInfo(data.plan);
      if (data.sites?.length > 0 && !selected) setSelected(data.sites[0].id);
    } catch (e) {
      toast.error('Failed to load: ' + e.message);
    } finally {
      setPageLoading(false);
    }
  }

  async function fetchStats() {
    setStatsLoading(true);
    try {
      const s    = new Date(dateRange.start + 'T00:00:00');
      const e    = new Date(dateRange.end   + 'T00:00:00');
      const days = Math.max(1, Math.round((e - s) / 86400000) + 1);
      const res  = await authFetch('stats', {
        qs: `site_id=${selected}&days=${days}&from=${dateRange.start}&to=${dateRange.end}`
      });
      const data = await res.json();
      if (!data.error) setStats(data);
    } catch { /* silent */ } finally {
      setStatsLoading(false);
    }
  }

  async function fetchSettings() {
    try {
      const res  = await authFetch('settings', { qs: `site_id=${selected}` });
      const data = await res.json();
      if (!data.error) { setSettings(data.settings); if (data.plan) setPlanInfo(data.plan); }
    } catch { /* silent */ }
  }

  async function fetchBlockedList(siteId) {
    if (!siteId) return;
    setBlockedLoading(true);
    try {
      const res = await authFetch('blocked_list', { qs: `site_id=${siteId}&limit=50` });
      if (res.status === 403 || res.status === 401) { setBlockedList([]); return; }
      const text = await res.text();
      try {
        const data = JSON.parse(text);
        setBlockedList(data.blocked || []);
      } catch {
        // API returned non-JSON (server error) — fail silently
        console.warn('[HapiGuard] blocked_list non-JSON response:', text.slice(0, 100));
        setBlockedList([]);
      }
    } catch { setBlockedList([]); }
    finally { setBlockedLoading(false); }
  }

  async function handleAddSite() {
    if (!newDomain.trim()) return;
    try {
      const res  = await authFetch('register', { method: 'POST', body: JSON.stringify({ domain: newDomain.trim() }) });
      const data = await res.json();
      if (data.error) { toast.error(data.error); return; }
      toast.success('Site registered');
      setNewDomain(''); setAddingDomain(false);
      await fetchSites();
      setSelected(data.site_id);
    } catch (e) { toast.error('Failed: ' + e.message); }
  }

  async function handleDeleteSite(siteId, domain) {
    if (!window.confirm(`Delete "${domain}"? All click data and settings will be removed.`)) return;
    try {
      const res  = await authFetch('delete_site', { method: 'POST', body: JSON.stringify({ site_id: siteId }) });
      const data = await res.json();
      if (data.error) { toast.error(data.error); return; }
      toast.success(`${domain} deleted`);
      setSites(prev => prev.filter(s => s.id !== siteId));
      if (selected === siteId) {
        const rem = sites.filter(s => s.id !== siteId);
        setSelected(rem[0]?.id || null);
        setStats(null); setSettings(null);
      }
    } catch (e) { toast.error('Failed: ' + e.message); }
  }

  async function handleSaveSettings(updates) {
    setSaving(true);
    try {
      const res  = await authFetch('settings', { method: 'PUT', body: JSON.stringify({ site_id: selected, ...updates }) });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setSettings(data.settings);
      toast.success('Settings saved');
    } catch (e) { toast.error('Failed: ' + e.message); } finally {
      setSaving(false);
    }
  }

  function copyApiKey(key) {
    navigator.clipboard.writeText(key);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
    toast.success('API key copied');
  }

  const currentSite = sites.find(s => s.id === selected);
  const userIsPro     = planInfo.isPro || isPro;
  const userIsStarter = ['starter','pro','business'].includes(planInfo.tier) || userIsPro;

  // ── FULL PAGE SKELETON (first load) ─────────────────────────────────────────
  if (pageLoading) return <PageSkeleton />;

  return (
    <div className="space-y-5 pb-8">

      {/* ── Header row ─────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-red-500 shrink-0" />
          <h1 className="text-xl font-bold text-foreground whitespace-nowrap">HapiGuard</h1>
        </div>

        {sites.length > 0 && selected && (
          <SiteSelector sites={sites} selected={selected} onSelect={setSelected} onDelete={handleDeleteSite} />
        )}

        <div className="flex-1 hidden sm:block" />

        {selected && (
          <DateRangePicker
            startDate={dateRange.start}
            endDate={dateRange.end}
            defaultPreset={dateRange.preset || 'today'}
            showComparison={false}
            onChange={({ start, end, preset }) => setDateRange({ start, end, preset: preset || 'custom' })}
          />
        )}

        {planInfo.site_limit && (
          <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
            planInfo.can_add === false
              ? 'bg-destructive/10 text-destructive'
              : 'bg-muted text-muted-foreground'
          }`}>
            {planInfo.sites_used || 0}/{planInfo.site_limit} sites
          </span>
        )}

        {planInfo.can_add === false ? (
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-amber-300 dark:border-amber-700
            bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 text-xs font-medium">
            Limit reached — <a href="/subscribe" className="underline font-semibold">Upgrade</a>
          </div>
        ) : (
          <button onClick={() => setAddingDomain(true)}
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-colors whitespace-nowrap
              bg-gray-900 hover:bg-gray-800 text-white dark:bg-gray-100 dark:hover:bg-white dark:text-gray-900">
            <Plus className="h-4 w-4" /> Add Site
          </button>
        )}
      </div>

      {/* ── Add site modal ──────────────────────────────────────────────────── */}
      {addingDomain && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-2xl shadow-2xl p-6 w-full max-w-md">
            <h2 className="text-lg font-bold text-foreground mb-1">Register a Site</h2>
            <p className="text-sm text-muted-foreground mb-4">Enter your domain to generate an API key.</p>
            <input type="text" value={newDomain} onChange={e => setNewDomain(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAddSite()}
              placeholder="yourblog.com" autoFocus
              className="w-full px-3 py-2.5 border border-border rounded-xl text-sm mb-4
                bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary" />
            <div className="flex gap-3">
              <button onClick={handleAddSite}
                className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl text-sm transition-colors">
                Register Site
              </button>
              <button onClick={() => { setAddingDomain(false); setNewDomain(''); }}
                className="flex-1 py-2.5 border border-border text-foreground hover:bg-muted font-semibold rounded-xl text-sm transition-colors">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Empty state ─────────────────────────────────────────────────────── */}
      {sites.length === 0 ? (
        <EmptyState onAdd={() => setAddingDomain(true)} />
      ) : currentSite && (
        <div className="space-y-5">

          {/* Row 1 — KPI stats */}
          {statsLoading ? <StatsSkeleton /> : stats && <StatsRow stats={stats} />}

          {/* Row 2 — Anomaly alerts (where API key used to be) */}
          {!statsLoading && stats?.alerts?.length > 0 && <AlertsCard alerts={stats.alerts} />}

          {/* Chart */}
          {!statsLoading && stats?.daily?.length > 0 && <ChartsSection data={stats.daily} />}

          {/* Settings */}
          {settings ? (
            <>
              <BasicSettingsCard settings={settings} saving={saving} onSave={handleSaveSettings} />
              <GeoBlockCard     settings={settings} saving={saving} isStarter={userIsStarter} isPro={userIsPro} onSave={handleSaveSettings} />
              <DeviceBlockCard  settings={settings} saving={saving} isPro={userIsPro} onSave={handleSaveSettings} blocked={blockedList} />
              <EmailAlertCard   settings={settings} saving={saving} isPro={userIsPro} onSave={handleSaveSettings} />
            </>
          ) : (
            <div className="space-y-5">
              <Skeleton className="h-48 w-full rounded-2xl" />
              <Skeleton className="h-28 w-full rounded-2xl" />
            </div>
          )}

          {/* Blocked Visitors — starter+ */}
          <BlockedVisitorsCard
            blocked={blockedList}
            loading={blockedLoading}
            tier={planInfo.tier || 'trial'}
          />

          {/* API key moved to bottom */}
          <ApiKeyCard site={currentSite} onCopy={copyApiKey} copied={copied} />
          <InstallCard site={currentSite} />
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// STATS ROW
// ═══════════════════════════════════════════════════════════════════════════════
function StatsRow({ stats }) {
  const { totals, active_blocks } = stats;
  const blockRate = totals.total_clicks > 0
    ? ((totals.blocked_clicks / totals.total_clicks) * 100).toFixed(1) : 0;

  const cards = [
    { label: 'Page Views',    value: (totals.page_views||0).toLocaleString(),   icon: <Eye className="h-4 w-4" />,              color: 'text-blue-500',    stripe: 'from-blue-500 to-blue-400' },
    { label: 'Sessions',      value: (totals.unique_visitors||0).toLocaleString(),icon: <Users className="h-4 w-4" />,          color: 'text-indigo-500',  stripe: 'from-indigo-500 to-indigo-400' },
    { label: 'Total Clicks',  value: (totals.total_clicks||0).toLocaleString(),  icon: <MousePointerClick className="h-4 w-4" />,color: 'text-violet-500', stripe: 'from-violet-500 to-violet-400' },
    { label: 'Blocked',       value: (totals.blocked_clicks||0).toLocaleString(),icon: <Ban className="h-4 w-4" />,             color: 'text-red-500',     stripe: 'from-red-500 to-rose-400' },
    { label: 'Block Rate',    value: `${blockRate}%`,                             icon: <ShieldAlert className="h-4 w-4" />,     color: parseFloat(blockRate)>5?'text-amber-500':'text-emerald-500', stripe: parseFloat(blockRate)>5?'from-amber-500 to-yellow-400':'from-emerald-500 to-teal-400' },
    { label: 'Active Bans',   value: (active_blocks||0).toLocaleString(),        icon: <ShieldOff className="h-4 w-4" />,       color: 'text-orange-500',  stripe: 'from-orange-500 to-amber-400' },
    { label: 'Avg Time→Click',value: formatTime(totals.avg_time_before_click_seconds), icon: <Clock className="h-4 w-4" />, color: 'text-teal-500', stripe: 'from-teal-500 to-cyan-400' },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 gap-3">
      {cards.map(c => (
        <div key={c.label}
          className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
          <div className="p-3">
            <div className={`flex items-center gap-1 mb-1.5 ${c.color}`}>
              {c.icon}
              <p className="text-[10px] uppercase tracking-wider font-medium opacity-80 leading-tight">{c.label}</p>
            </div>
            <p className={`text-xl font-bold leading-none ${c.color}`}>{c.value}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CHARTS
// ═══════════════════════════════════════════════════════════════════════════════
function ChartsSection({ data }) {
  const [tab, setTab] = useState('traffic');

  return (
    <div className="rounded-2xl border border-border bg-card shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-foreground">Analytics</h3>
        <div className="flex gap-1 p-1 bg-muted rounded-xl">
          {[{ id:'traffic',label:'Traffic'},{ id:'clicks',label:'Clicks'},{ id:'time',label:'Avg Time'}].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                tab === t.id ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <ResponsiveContainer width="100%" height={180}>
        {tab === 'traffic' ? (
          <AreaChart data={data} margin={{ top:5, right:10, left:-20, bottom:0 }}>
            <defs>
              <linearGradient id="gPV" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2}/>
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="gSess" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#6366f1" stopOpacity={0.2}/>
                <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false}/>
            <XAxis dataKey="date" tick={{fontSize:11,fill:'var(--muted-foreground)'}} axisLine={false} tickLine={false}
              tickFormatter={d => new Date(d).toLocaleDateString('en',{month:'short',day:'numeric'})}/>
            <YAxis tick={{fontSize:11,fill:'var(--muted-foreground)'}} axisLine={false} tickLine={false}/>
            <Tooltip contentStyle={{background:'var(--card)',border:'1px solid var(--border)',borderRadius:'10px',fontSize:'12px',color:'var(--foreground)'}}
              labelFormatter={d => new Date(d).toLocaleDateString('en',{month:'short',day:'numeric'})}/>
            <Legend iconSize={8} wrapperStyle={{fontSize:'11px'}}/>
            <Area type="monotone" dataKey="page_views" name="Page Views" stroke="#3b82f6" strokeWidth={1.5} fill="url(#gPV)" dot={false}/>
            <Area type="monotone" dataKey="unique_visitors" name="Sessions" stroke="#6366f1" strokeWidth={1.5} fill="url(#gSess)" dot={false}/>
          </AreaChart>
        ) : tab === 'clicks' ? (
          <BarChart data={data} margin={{ top:5, right:10, left:-20, bottom:0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false}/>
            <XAxis dataKey="date" tick={{fontSize:11,fill:'var(--muted-foreground)'}} axisLine={false} tickLine={false}
              tickFormatter={d => new Date(d).toLocaleDateString('en',{month:'short',day:'numeric'})}/>
            <YAxis tick={{fontSize:11,fill:'var(--muted-foreground)'}} axisLine={false} tickLine={false}/>
            <Tooltip contentStyle={{background:'var(--card)',border:'1px solid var(--border)',borderRadius:'10px',fontSize:'12px',color:'var(--foreground)'}}
              labelFormatter={d => new Date(d).toLocaleDateString('en',{month:'short',day:'numeric'})}/>
            <Legend iconSize={8} wrapperStyle={{fontSize:'11px'}}/>
            <Bar dataKey="total_clicks" name="Total Clicks" fill="#8b5cf6" radius={[3,3,0,0]}/>
            <Bar dataKey="blocked_clicks" name="Blocked" fill="#ef4444" radius={[3,3,0,0]}/>
          </BarChart>
        ) : (
          <AreaChart data={data} margin={{ top:5, right:10, left:-20, bottom:0 }}>
            <defs>
              <linearGradient id="gTime" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.2}/>
                <stop offset="95%" stopColor="#14b8a6" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false}/>
            <XAxis dataKey="date" tick={{fontSize:11,fill:'var(--muted-foreground)'}} axisLine={false} tickLine={false}
              tickFormatter={d => new Date(d).toLocaleDateString('en',{month:'short',day:'numeric'})}/>
            <YAxis tick={{fontSize:11,fill:'var(--muted-foreground)'}} axisLine={false} tickLine={false}
              tickFormatter={v => formatTime(v)}/>
            <Tooltip contentStyle={{background:'var(--card)',border:'1px solid var(--border)',borderRadius:'10px',fontSize:'12px',color:'var(--foreground)'}}
              labelFormatter={d => new Date(d).toLocaleDateString('en',{month:'short',day:'numeric'})}
              formatter={v => [formatTime(v),'Avg Time']}/>
            <Area type="monotone" dataKey="avg_time_before_click_seconds" name="Avg Time"
              stroke="#14b8a6" strokeWidth={1.5} fill="url(#gTime)" dot={false}/>
          </AreaChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// API KEY CARD
// ═══════════════════════════════════════════════════════════════════════════════
function ApiKeyCard({ site, onCopy, copied }) {
  const [visible, setVisible] = useState(false);
  const masked = site.api_key
    ? site.api_key.slice(0, 6) + '•'.repeat(Math.max(0, site.api_key.length - 10)) + site.api_key.slice(-4)
    : '';
  return (
    <div className="rounded-2xl border border-border bg-card shadow-sm p-5">
      <h3 className="text-sm font-bold text-foreground mb-1">API Key</h3>
      <p className="text-xs text-muted-foreground mb-3">Paste into your WordPress plugin to connect this site.</p>
      <div className="flex items-center gap-2">
        <code className="flex-1 px-3 py-2.5 rounded-xl text-sm font-mono truncate
          bg-muted text-foreground border border-border select-all">
          {visible ? site.api_key : masked}
        </code>
        <button onClick={() => setVisible(v => !v)}
          className="px-3 py-2.5 rounded-xl border border-border hover:bg-muted
            transition-colors flex items-center gap-1.5 text-sm text-foreground shrink-0"
          title={visible ? 'Hide API key' : 'Show API key'}>
          {visible ? <EyeOff className="h-4 w-4"/> : <Eye className="h-4 w-4"/>}
        </button>
        <button onClick={() => onCopy(site.api_key)}
          className="px-3 py-2.5 rounded-xl border border-border hover:bg-muted
            transition-colors flex items-center gap-1.5 text-sm text-foreground shrink-0">
          {copied ? <Check className="h-4 w-4 text-emerald-500"/> : <Copy className="h-4 w-4"/>}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// BASIC SETTINGS
// ═══════════════════════════════════════════════════════════════════════════════
function BasicSettingsCard({ settings, saving, onSave }) {
  const [local, setLocal] = useState(settings);
  useEffect(() => setLocal(settings), [settings]);
  const update = (k, v) => setLocal(p => ({ ...p, [k]: v }));

  return (
    <div className="rounded-2xl border border-border bg-card shadow-sm p-5">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-sm font-bold text-foreground">Protection Settings</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Click limits applied to all visitors</p>
        </div>
        <Toggle enabled={local.enabled} onChange={v => update('enabled', v)} />
      </div>
      <div className="grid sm:grid-cols-3 gap-4 mb-5">
        <SettingField label="Click Limit"          hint="Max clicks before blocking"    type="number" min={1} max={10}   value={local.click_limit}        onChange={v => update('click_limit',parseInt(v))}/>
        <SettingField label="Time Window (mins)"   hint="Click count resets after this" type="number" min={5} max={1440} value={local.window_minutes}     onChange={v => update('window_minutes',parseInt(v))}/>
        <SettingField label="Ban Duration (hours)" hint="How long to hide ads"          type="number" min={1} max={168}  value={local.ban_duration_hours}  onChange={v => update('ban_duration_hours',parseInt(v))}/>
      </div>
      <SaveButton saving={saving} onClick={() => onSave({
        enabled: local.enabled, click_limit: local.click_limit,
        window_minutes: local.window_minutes, ban_duration_hours: local.ban_duration_hours,
      })}/>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// GEO BLOCKING
// ═══════════════════════════════════════════════════════════════════════════════
function GeoBlockCard({ settings, saving, isStarter, isPro, onSave }) {
  const [enabled,   setEnabled]   = useState(settings.geo_block_enabled || false);
  const [allowOnly, setAllowOnly] = useState(settings.geo_allow_only    || false);
  const [countries, setCountries] = useState(settings.geo_blocked_countries || []);
  const [search,    setSearch]    = useState('');

  useEffect(() => {
    setEnabled(settings.geo_block_enabled || false);
    setAllowOnly(settings.geo_allow_only  || false);
    setCountries(settings.geo_blocked_countries || []);
  }, [settings]);

  const filtered = COUNTRIES.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) || c.code.toLowerCase().includes(search.toLowerCase())
  );
  const toggleCountry = code => setCountries(p => p.includes(code) ? p.filter(c=>c!==code) : [...p,code]);

  if (!isStarter && !isPro) return (
    <ProLockedCard title="Geo-blocking" icon={<Globe className="h-5 w-5 text-amber-500"/>}
      description="Block specific countries from seeing your ads — or allow only your target markets. Available on Starter and above."/>
  );

  return (
    <div className="rounded-2xl border border-border bg-card shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
            <Globe className="h-4 w-4 text-blue-500"/>
            Geo-blocking
            <span className="text-xs px-1.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 font-semibold">Pro</span>
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">Block or allow specific countries</p>
        </div>
        <Toggle enabled={enabled} onChange={setEnabled}/>
      </div>
      <div className={!enabled ? 'opacity-60 pointer-events-none select-none' : ''}>
        <div className="flex gap-2 mb-3 p-1 bg-muted rounded-xl w-fit">
          {[{v:false,l:'Block list'},{v:true,l:'Allow only'}].map(({v,l}) => (
            <button key={l} onClick={() => setAllowOnly(v)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                allowOnly===v ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'
              }`}>{l}</button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mb-2">
          {allowOnly ? 'Only these countries will see your ads:' : 'These countries will be blocked:'}
          {countries.length === 0 && <span className="italic"> — none selected</span>}
        </p>
        {countries.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {countries.map(code => (
              <span key={code} className="flex items-center gap-1 px-2 py-1 bg-foreground text-background text-xs rounded-lg font-medium">
                {COUNTRIES.find(x=>x.code===code)?.name || code}
                <button onClick={()=>toggleCountry(code)} className="ml-0.5 hover:text-red-400">×</button>
              </span>
            ))}
          </div>
        )}
        <input type="text" placeholder="Search countries…" value={search} onChange={e => setSearch(e.target.value)}
          className="w-full px-3 py-2 border border-border rounded-xl text-sm mb-2
            bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"/>
        <div className="max-h-52 overflow-y-auto border border-border rounded-xl divide-y divide-border">
          {filtered.length === 0
            ? <p className="text-center py-4 text-xs text-muted-foreground">No countries match your search</p>
            : filtered.map(c => (
              <button key={c.code} onClick={()=>toggleCountry(c.code)}
                className={`w-full flex items-center justify-between px-3 py-2 text-sm text-left transition-colors hover:bg-muted ${
                  countries.includes(c.code) ? 'bg-primary/10 font-semibold' : ''
                }`}>
                <span className={countries.includes(c.code) ? 'text-primary' : 'text-foreground'}>{c.name}</span>
                <span className={`text-xs font-mono ${countries.includes(c.code) ? 'text-primary' : 'text-muted-foreground'}`}>{c.code}</span>
              </button>
            ))
          }
        </div>
        {!enabled && (
          <p className="text-xs text-muted-foreground mt-2 italic">Toggle on to activate geo-blocking.</p>
        )}
      </div>
      <div className="mt-4">
        <SaveButton saving={saving} onClick={() => onSave({
          geo_block_enabled: enabled, geo_allow_only: allowOnly, geo_blocked_countries: countries,
        })}/>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// EMAIL ALERTS
// ═══════════════════════════════════════════════════════════════════════════════
function EmailAlertCard({ settings, saving, isPro, onSave }) {
  const [enabled,   setEnabled]   = useState(settings.notify_email        || false);
  const [email,     setEmail]     = useState(settings.notify_email_address || '');
  const [threshold, setThreshold] = useState(settings.notify_threshold    || 5);

  useEffect(() => {
    setEnabled(settings.notify_email        || false);
    setEmail(settings.notify_email_address   || '');
    setThreshold(settings.notify_threshold   || 5);
  }, [settings]);

  if (!isPro) return (
    <ProLockedCard title="Email Alerts" icon={<Mail className="h-5 w-5 text-amber-500"/>}
      description="Get notified when a click spike is detected on any of your sites."/>
  );

  return (
    <div className="rounded-2xl border border-border bg-card shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
            <Mail className="h-4 w-4 text-purple-500"/>
            Email Alerts
            <span className="text-xs px-1.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 font-semibold">Pro</span>
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">Notified on suspicious activity</p>
        </div>
        <Toggle enabled={enabled} onChange={setEnabled}/>
      </div>
      {enabled && (
        <div className="space-y-4">
          <SettingField label="Alert email address" hint="We'll email when threshold is hit"
            type="email" value={email} onChange={setEmail} placeholder="you@yourblog.com"/>
          <SettingField label="Alert threshold (clicks)" hint="Clicks before we email you"
            type="number" min={2} max={50} value={threshold} onChange={v => setThreshold(parseInt(v))}/>
        </div>
      )}
      <div className="mt-4">
        <SaveButton saving={saving} onClick={() => onSave({
          notify_email: enabled, notify_email_address: email, notify_threshold: threshold,
        })}/>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ALERTS CARD
// ═══════════════════════════════════════════════════════════════════════════════
function AlertsCard({ alerts }) {
  const sev = {
    high:   { row: 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/60',       icon: 'text-red-600 dark:text-red-400',   label: 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200',       dot: 'bg-red-500' },
    medium: { row: 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/60',   icon: 'text-amber-600 dark:text-amber-400', label: 'bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-200', dot: 'bg-amber-500' },
    low:    { row: 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-950/60',       icon: 'text-blue-600 dark:text-blue-400',   label: 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-200',     dot: 'bg-blue-500' },
  };
  return (
    <div className="rounded-2xl border border-border bg-card shadow-sm p-5">
      <h3 className="text-sm font-bold text-foreground mb-4 flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-500"/>
        Anomaly Alerts
        <span className="ml-1.5 px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-xs font-bold">
          {alerts.length} unread
        </span>
      </h3>
      <div className="space-y-2.5">
        {alerts.map(alert => {
          const s = sev[alert.severity] || sev.medium;
          return (
            <div key={alert.id} className={`rounded-xl border px-4 py-3 flex items-start gap-3 ${s.row}`}>
              <span className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${s.dot}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className={`text-xs font-bold uppercase tracking-wide capitalize ${s.icon}`}>
                    {(alert.alert_type || '').replace(/_/g,' ')}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase ${s.label}`}>
                      {alert.severity}
                    </span>
                    <span className={`text-xs opacity-60 ${s.icon}`}>
                      {new Date(alert.created_at).toLocaleDateString('en',{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'})}
                    </span>
                  </div>
                </div>
                {alert.details?.click_count && (
                  <div className="mt-1 overflow-hidden">
                    <p className={`text-xs opacity-75 ${s.icon}`}>
                      {alert.details.click_count} suspicious click{alert.details.click_count > 1 ? 's' : ''} from one visitor
                    </p>
                    {alert.details.page_url && (
                      <p className={`text-xs opacity-60 ${s.icon} truncate`}>
                        · {(() => { try { return new URL(alert.details.page_url).pathname; } catch { return alert.details.page_url; } })()}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// INSTALL CARD
// ═══════════════════════════════════════════════════════════════════════════════
function InstallCard({ site }) {
  const [tab, setTab] = useState('wordpress');
  const snippet = `<script src="https://cdn.revhapi.com/hapiguard.min.js"\n  data-api-key="${site.api_key}"\n  data-api-url="https://app.revhapi.com/api/hapiguard"\n  data-click-limit="2"\n  async></script>`;

  return (
    <div className="rounded-2xl border border-border bg-card shadow-sm p-5">
      <h3 className="text-sm font-bold text-foreground mb-1">Install HapiGuard</h3>
      <p className="text-xs text-muted-foreground mb-4">Add protection to your site in under 2 minutes</p>
      <div className="flex gap-1 p-1 bg-muted rounded-xl mb-4 w-fit">
        {['wordpress','snippet'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              tab===t ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'
            }`}>
            {t==='wordpress' ? 'WordPress Plugin' : 'Drop-in Snippet'}
          </button>
        ))}
      </div>
      {tab==='wordpress' ? (
        <div className="space-y-3">
          <Step n={1} text='Install "HapiGuard – Invalid Click Protector" from WordPress.org'/>
          <Step n={2} text="Go to Settings → HapiGuard in your WordPress admin"/>
          <Step n={3} text={`Paste your API key: ${site.api_key}`}/>
          <Step n={4} text="Save. Protection is active immediately."/>
          <a href="https://wordpress.org/plugins/hapiguard" target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline font-medium mt-2">
            Download Plugin <ExternalLink className="h-3.5 w-3.5"/>
          </a>
        </div>
      ) : (
        <div className="space-y-3">
          <Step n={1} text="Paste before </head> on every page with ads:"/>
          <pre className="bg-muted border border-border rounded-xl p-3 text-xs font-mono text-foreground overflow-x-auto whitespace-pre-wrap">{snippet}</pre>
          <Step n={2} text="Works on Ghost, Blogger, Next.js, plain HTML — any platform."/>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SHARED COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════
function EmptyState({ onAdd }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="w-16 h-16 rounded-2xl bg-destructive/10 border border-destructive/20 flex items-center justify-center mb-4">
        <ShieldAlert className="h-8 w-8 text-destructive/60"/>
      </div>
      <h2 className="text-xl font-bold text-foreground mb-2">No sites protected yet</h2>
      <p className="text-sm text-muted-foreground max-w-xs mb-6">Register your first site to start protecting your AdSense account.</p>
      <button onClick={onAdd}
        className="flex items-center gap-2 px-5 py-3 font-semibold rounded-xl text-sm transition-colors
        bg-gray-900 hover:bg-gray-800 text-white dark:bg-gray-100 dark:hover:bg-white dark:text-gray-900">
        <Plus className="h-4 w-4"/> Register Your First Site
      </button>
    </div>
  );
}

function ProLockedCard({ title, icon, description }) {
  return (
    <div className="rounded-2xl border border-dashed border-border bg-card/60 p-5">
      <div className="flex items-center gap-3 mb-2">
        {icon}
        <h3 className="text-sm font-bold text-foreground">{title}</h3>
        <Lock className="h-3.5 w-3.5 text-muted-foreground ml-auto"/>
        <span className="text-xs px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 font-semibold">Pro</span>
      </div>
      <p className="text-xs text-muted-foreground mb-3">{description}</p>
      <a href="/subscribe" className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-600 hover:text-amber-700 dark:text-amber-400">
        Upgrade to Pro →
      </a>
    </div>
  );
}

function Toggle({ enabled, onChange }) {
  return (
    <button onClick={() => onChange(!enabled)}
      className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${enabled ? 'bg-emerald-500' : 'bg-muted-foreground/30'}`}>
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white dark:bg-gray-100 rounded-full shadow transition-transform ${enabled ? 'translate-x-5' : 'translate-x-0'}`}/>
    </button>
  );
}

function SettingField({ label, hint, type, min, max, value, onChange, placeholder }) {
  return (
    <div>
      <label className="text-xs font-semibold text-foreground block mb-1">{label}</label>
      <input type={type} min={min} max={max} value={value} placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-border rounded-xl text-sm
          bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"/>
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

function SaveButton({ saving, onClick }) {
  return (
    <button onClick={onClick} disabled={saving}
      className="w-full py-2.5 font-semibold rounded-xl text-sm transition-colors
        flex items-center justify-center gap-2 disabled:opacity-50
        bg-gray-900 hover:bg-gray-800 text-white
        dark:bg-gray-100 dark:hover:bg-white dark:text-gray-900">
      {saving && <RefreshCw className="h-4 w-4 animate-spin"/>}
      {saving ? 'Saving…' : 'Save Settings'}
    </button>
  );
}

function Step({ n, text }) {
  return (
    <div className="flex items-start gap-3">
      <span className="w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center shrink-0 mt-0.5
        bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900">{n}</span>
      <p className="text-sm text-foreground">{text}</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BLOCKED VISITORS CARD — starter+ only
// Shows country, device OS, device type for each blocked fingerprint
// ─────────────────────────────────────────────────────────────────────────────
function BlockedVisitorsCard({ blocked, loading, tier }) {
  const allowedTiers = ['starter', 'pro', 'business'];
  const hasAccess    = allowedTiers.includes(tier);

  const FLAG = code => code
    ? String.fromCodePoint(...[...code.toUpperCase()].map(c => 0x1F1E6 - 65 + c.charCodeAt(0)))
    : '🌐';

  const OS_ICON = os => ({
    'Windows': '🪟', 'iOS': '🍎', 'Android': '🤖',
    'macOS': '🍏', 'Linux': '🐧', 'ChromeOS': '🌐', 'Bot': '🤖',
  }[os] || '💻');

  const DEVICE_BADGE = type => ({
    'mobile':  { label: 'Mobile',  cls: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' },
    'tablet':  { label: 'Tablet',  cls: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300' },
    'desktop': { label: 'Desktop', cls: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300' },
    'bot':     { label: 'Bot',     cls: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300' },
  }[type] || { label: type || '—', cls: 'bg-muted text-muted-foreground' });

  function fmtExpiry(expires_at) {
    if (!expires_at) return '—';
    const d = new Date(expires_at);
    const now = new Date();
    if (d < now) return 'Expired';
    const h = Math.ceil((d - now) / 3600000);
    return h < 24 ? `${h}h remaining` : `${Math.ceil(h/24)}d remaining`;
  }

  return (
    <div className="rounded-2xl border border-border bg-card shadow-sm p-5">
      <h3 className="text-sm font-bold text-foreground mb-1 flex items-center gap-2">
        <Ban className="h-4 w-4 text-red-500" />
        Blocked Visitors
        {hasAccess && blocked.length > 0 && (
          <span className="ml-auto px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs font-semibold">
            {blocked.length} blocked
          </span>
        )}
      </h3>
      <p className="text-xs text-muted-foreground mb-4">
        Visitors who exceeded the click limit — with country, device and OS details.
      </p>

      {!hasAccess ? (
        <div className="rounded-xl border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/50 p-4 text-sm text-amber-900 dark:text-amber-200 flex items-start gap-3">
          <span className="text-lg">🔒</span>
          <div>
            <p className="font-semibold mb-0.5">Starter plan required</p>
            <p className="text-xs opacity-80">Upgrade to Starter or higher to see country, IP hash, device OS and type for each blocked visitor.</p>
          </div>
        </div>
      ) : loading ? (
        <div className="space-y-2">
          {[1,2,3].map(i => <div key={i} className="h-12 bg-muted rounded-xl animate-pulse" />)}
        </div>
      ) : blocked.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <Ban className="h-8 w-8 mx-auto mb-2 opacity-30" />
          <p className="text-sm font-medium text-foreground mb-1">No blocked visitors yet</p>
          <p className="text-xs max-w-xs mx-auto">
            Visitors appear here once HapiGuard blocks a repeat ad clicker.
            Make sure the plugin is installed and your API key is saved in WordPress.
          </p>
        </div>
      ) : (
        <div className="space-y-2 max-h-[420px] overflow-y-auto pr-0.5">
          {blocked.map((b, i) => {
            const badge = DEVICE_BADGE(b.device_type);
            const path  = b.page_url
              ? (() => { try { return new URL(b.page_url).pathname; } catch { return b.page_url; } })()
              : null;
            const isExpiredBan = b.expires_at && new Date(b.expires_at) < new Date();
            return (
              <div key={i}
                className={`rounded-xl border p-3 transition-colors ${
                  isExpiredBan
                    ? 'border-border bg-muted/30 dark:bg-background/40 opacity-60'
                    : 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-gray-800'
                }`}>
                {/* Row 1 — flag + identifier + expiry */}
                <div className="flex items-start gap-2.5 mb-2">
                  <span className="text-xl leading-none flex-shrink-0 mt-0.5"
                    title={b.country_code || 'Unknown'}>
                    {FLAG(b.country_code)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-mono text-foreground dark:text-white leading-tight truncate">
                      {b.ip_hash ? b.ip_hash.slice(0,16) + '…' : (b.fingerprint || '').slice(0,12) + '…'}
                    </p>
                    {path && (
                      <p className="text-[10px] text-muted-foreground truncate mt-0.5">{path}</p>
                    )}
                  </div>
                  <span className={`text-[10px] font-semibold flex-shrink-0 ml-auto ${
                    isExpiredBan ? 'text-muted-foreground' : 'text-red-600 dark:text-red-400'
                  }`}>
                    {fmtExpiry(b.expires_at)}
                  </span>
                </div>
                {/* Row 2 — badges */}
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Country code pill */}
                  {b.country_code && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold
                      bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-100">
                      {b.country_code}
                    </span>
                  )}
                  {/* OS */}
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-medium flex items-center gap-1
                    bg-muted dark:bg-gray-700 text-muted-foreground dark:text-gray-100">
                    {OS_ICON(b.device_os)}
                    <span>{b.device_os || '—'}</span>
                  </span>
                  {/* Device type */}
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${badge.cls}`}>
                    {badge.label}
                  </span>
                  {/* Click count */}
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold ml-auto
                    bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300">
                    {b.click_count} clicks
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DEVICE BLOCK CARD — Pro+ only
// Block by device fingerprint ID, OS type, or IP hash
// ─────────────────────────────────────────────────────────────────────────────
const OS_OPTIONS = ['Windows','macOS','iOS','Android','Linux','ChromeOS','Bot'];

function DeviceBlockCard({ settings, saving, isPro, onSave, blocked }) {
  const [deviceIds,  setDeviceIds]  = useState(settings.block_device_ids  || []);
  const [osTypes,    setOsTypes]    = useState(settings.block_os_types    || []);
  const [ipHashes,   setIpHashes]   = useState(settings.block_ip_hashes   || []);
  const [newId,      setNewId]      = useState('');
  const [newIp,      setNewIp]      = useState('');

  useEffect(() => {
    setDeviceIds(settings.block_device_ids  || []);
    setOsTypes  (settings.block_os_types    || []);
    setIpHashes (settings.block_ip_hashes   || []);
  }, [settings]);

  if (!isPro) return (
    <ProLockedCard title="Device & IP Blocking" icon={<Monitor className="h-5 w-5 text-purple-500"/>}
      description="Block specific device IDs, operating systems, and IP addresses. Available on Pro and above." />
  );

  const toggleOs = os => setOsTypes(p => p.includes(os) ? p.filter(o => o !== os) : [...p, os]);

  const addDeviceId = () => {
    const v = newId.trim();
    if (!v || deviceIds.includes(v)) return;
    setDeviceIds(p => [...p, v]);
    setNewId('');
  };

  const addIpHash = () => {
    const v = newIp.trim().slice(0, 16); // normalise to 16 chars
    if (!v || ipHashes.includes(v)) return;
    setIpHashes(p => [...p, v]);
    setNewIp('');
  };

  const handleSave = () => onSave({
    block_device_ids: deviceIds,
    block_os_types:   osTypes,
    block_ip_hashes:  ipHashes,
  });

  // Quick-add from blocked list
  const addFromBlocked = (b) => {
    if (b.fingerprint && !deviceIds.includes(b.fingerprint)) setDeviceIds(p => [...p, b.fingerprint]);
    if (b.ip_hash) {
      const h = b.ip_hash.slice(0,16);
      if (!ipHashes.includes(h)) setIpHashes(p => [...p, h]);
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-card shadow-sm p-5 space-y-5">
      <div>
        <h3 className="text-sm font-bold text-foreground flex items-center gap-2 mb-0.5">
          <Monitor className="h-4 w-4 text-purple-500" /> Device & IP Blocking
          <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 font-semibold">Pro</span>
        </h3>
        <p className="text-xs text-muted-foreground">Block specific device fingerprints, OS types, or IP hashes from seeing your ads.</p>
      </div>

      {/* OS type blocking */}
      <div>
        <label className="text-xs font-semibold text-foreground block mb-2">Block by Operating System</label>
        <div className="flex flex-wrap gap-2">
          {OS_OPTIONS.map(os => (
            <button key={os} onClick={() => toggleOs(os)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                osTypes.includes(os)
                  ? 'bg-red-500 text-white border-red-500'
                  : 'bg-background text-foreground border-border hover:bg-muted dark:hover:bg-muted'
              }`}>
              {os}
            </button>
          ))}
        </div>
        {osTypes.length > 0 && (
          <p className="text-[10px] text-red-600 dark:text-red-400 mt-1.5">
            🚫 Blocking: {osTypes.join(', ')}
          </p>
        )}
      </div>

      {/* Device ID blocking */}
      <div>
        <label className="text-xs font-semibold text-foreground block mb-2">Block by Device ID (Fingerprint)</label>
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            value={newId}
            onChange={e => setNewId(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addDeviceId()}
            placeholder="Paste fingerprint from blocked list…"
            className="flex-1 px-3 py-1.5 border border-border rounded-lg text-xs font-mono bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
          <button onClick={addDeviceId}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary text-primary-foreground hover:opacity-90 transition-opacity">
            Add
          </button>
        </div>
        {deviceIds.length > 0 && (
          <div className="space-y-1.5 max-h-32 overflow-y-auto">
            {deviceIds.map(id => (
              <div key={id} className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-muted text-xs font-mono">
                <span className="flex-1 truncate text-foreground">{id}</span>
                <button onClick={() => setDeviceIds(p => p.filter(i => i !== id))}
                  className="text-red-500 hover:text-red-600 flex-shrink-0 font-bold">✕</button>
              </div>
            ))}
          </div>
        )}
        {/* Quick-add from recent blocked list */}
        {blocked.length > 0 && (
          <div className="mt-2 pt-2 border-t border-border">
            <p className="text-[10px] text-muted-foreground mb-1.5">Quick add from recent blocked visitors:</p>
            <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
              {blocked.filter(b => b.fingerprint && !deviceIds.includes(b.fingerprint)).slice(0,8).map((b,i) => (
                <button key={i} onClick={() => addFromBlocked(b)}
                  className="px-2 py-1 rounded-lg text-[10px] font-mono border border-border bg-background hover:bg-muted text-foreground transition-colors truncate max-w-[120px]"
                  title={`${b.country_code || ''} · ${b.device_os || ''} · ${b.click_count} clicks`}>
                  + {(b.fingerprint || '').slice(0,10)}…
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* IP hash blocking */}
      <div>
        <label className="text-xs font-semibold text-foreground block mb-1">Block by IP Hash</label>
        <p className="text-[10px] text-muted-foreground mb-2">IP hashes are anonymised — paste the first 16 characters from the blocked visitors list.</p>
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            value={newIp}
            onChange={e => setNewIp(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addIpHash()}
            placeholder="e.g. a1b2c3d4e5f6g7h8"
            maxLength={16}
            className="flex-1 px-3 py-1.5 border border-border rounded-lg text-xs font-mono bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
          <button onClick={addIpHash}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary text-primary-foreground hover:opacity-90 transition-opacity">
            Add
          </button>
        </div>
        {ipHashes.length > 0 && (
          <div className="space-y-1.5 max-h-28 overflow-y-auto">
            {ipHashes.map(h => (
              <div key={h} className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-muted text-xs font-mono">
                <span className="flex-1 truncate text-foreground">{h}…</span>
                <button onClick={() => setIpHashes(p => p.filter(i => i !== h))}
                  className="text-red-500 hover:text-red-600 flex-shrink-0 font-bold">✕</button>
              </div>
            ))}
          </div>
        )}
      </div>

      <button onClick={handleSave} disabled={saving}
        className="w-full py-2.5 font-semibold rounded-xl text-sm transition-colors
          flex items-center justify-center gap-2 disabled:opacity-50
          bg-gray-900 hover:bg-gray-800 text-white dark:bg-gray-100 dark:hover:bg-white dark:text-gray-900">
        {saving ? <><RefreshCw className="h-4 w-4 animate-spin"/>Saving…</> : <><Save className="h-4 w-4"/>Save Device Rules</>}
      </button>
    </div>
  );
}
